<script setup lang="ts">
  import {
    cardContainerClass,
    cardContentClass,
    cardDescriptionClass,
    cardDisabledClass,
    cardIconClass,
    cardIconContainerClass,
    cardIconSelectedClass,
    cardReadonlyClass,
    cardSelectedClass,
    cardSelectionContainerIconClass,
    cardSelectionIconClass,
    cardTitleClass,
    cardSingleCardClass,
    cardSmallGapClass,
    textClass,
  } from './SelectableCard.css';
  import { FontIcon } from '../../FontIcon';
  import { Tooltip } from '../../Tooltip';
  import { PlacementOptions } from '../../utils/placement';
  import { computed } from 'vue';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { interactableClass } from '@tundr/theme';
  import {
    type SelectableCardProps,
    SelectableCardVariant,
  } from './SelectableCard.types';

  const props = withDefaults(defineProps<SelectableCardProps>(), {
    selectable: false,
    singleCard: false,
    variant: SelectableCardVariant.DEFAULT,
  });

  const isInteractive = computed(
    () => !props.selected && !props.readonly && !props.disabled,
  );

  const cardContainerClasses = computed(() => ({
    [cardContainerClass[props.variant]]: true,
    [cardSingleCardClass]: props.singleCard,
    [interactableClass]: isInteractive.value,
    [cardReadonlyClass]: props.readonly,
    [cardDisabledClass]: props.disabled,
    [cardSelectedClass[props.variant]]: props.selected,
    [cardSmallGapClass]: props.smallGap,
  }));

  const iconClasses = computed(() => ({
    [cardIconContainerClass[props.variant]]: true,
    [cardIconSelectedClass]: props.selected,
  }));

  const emits = defineEmits<{
    click: [event?: Event];
    middleClick: [event?: MouseEvent];
  }>();

  const onClick = () => {
    if (isInteractive.value) {
      emits('click');
    }
  };

  const onMiddleClick = () => {
    if (isInteractive.value) {
      emits('middleClick');
    }
  };
</script>

<template>
  <Tooltip
    :label="tooltipText"
    :description="tooltipDescription"
    :placement="PlacementOptions.LEFT"
  >
    <!-- biome-ignore lint/a11y/noStaticElementInteractions: button semantics are bound conditionally on isInteractive, which Biome cannot resolve statically -->
    <div
      :data-test-id="testId"
      :id="id || testId"
      :class="cardContainerClasses"
      :role="isInteractive ? 'button' : undefined"
      :tabindex="isInteractive ? 0 : undefined"
      @click="onClick"
      @auxclick="onMiddleClick"
      @keydown.enter="onClick"
      @keydown.space.prevent="onClick"
    >
      <slot name="icon">
        <div :class="iconClasses">
          <FontIcon
            v-if="iconName"
            :icon-name="iconName as TUNDR_ICONS"
            :class="cardIconClass"
          />
          <div v-if="text" :class="cardIconClass">
            <p :class="textClass">{{ text }}</p>
          </div>
        </div>
      </slot>

      <div :class="cardContentClass">
        <div :class="cardTitleClass">{{ title }}</div>
        <div :class="cardDescriptionClass">
          {{ description }}
          <slot name="description" />
        </div>
      </div>
      <div
        v-if="!readonly && selectable"
        :class="cardSelectionContainerIconClass"
      >
        <FontIcon
          :class="cardSelectionIconClass"
          :icon-name="TUNDR_ICONS.CHECK"
          v-if="selected"
        />
      </div>
    </div>
  </Tooltip>
</template>
